import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import MainNavbar from '../../components/Navbar/MainNavbar'
import Typography from '../../components/Typography'
import { theme } from '../../theme'
import axiosClient from '../../api/axiosClient'
import { useParams, useSearchParams, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import HelmetIcon from '../../components/Icons/HelmetIcon'
import Button from '../../components/Button/Button'
import BackIcon from '../../components/Icons/BackIcon'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

function UserConnections() {
  const { t } = useTranslation('connections')
  const { userId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const initialTabParam = searchParams.get('tab')
  const initialTab = initialTabParam === 'following' || initialTabParam === 'mutual' ? initialTabParam : 'followers'
  const [activeTab, setActiveTab] = useState(initialTab)
  const [profileUser, setProfileUser] = useState(null)
  const [followers, setFollowers] = useState([])
  const [following, setFollowing] = useState([])
  const [followersCursor, setFollowersCursor] = useState(null)
  const [followingCursor, setFollowingCursor] = useState(null)
  const [followersHasMore, setFollowersHasMore] = useState(true)
  const [followingHasMore, setFollowingHasMore] = useState(true)
  const { user, followingSet, follow, unfollow } = useAuth()
  const [myFollowingSet, setMyFollowingSet] = useState(new Set())
  const isOwn = Boolean(user?.id && user?.id === userId)
  const limit = 20

  useEffect(() => {
    const from = location.state?.from || searchParams.get('from')
    const eventId = location.state?.eventId || searchParams.get('eventId')
    const slug = location.state?.slug || searchParams.get('slug')
    const next = new URLSearchParams()
    next.set('tab', activeTab)
    if (from) next.set('from', from)
    if (eventId) next.set('eventId', eventId)
    if (slug) next.set('slug', slug)
    setSearchParams(next)
  }, [activeTab])

  useEffect(() => {
    const tab = searchParams.get('tab') === 'following' ? 'following' : 'followers'
    const nextTabParam = searchParams.get('tab')
    const nextTab = nextTabParam === 'following' || nextTabParam === 'mutual' ? nextTabParam : 'followers'
    setActiveTab(nextTab)
  }, [searchParams])

  useEffect(() => {
    const loadFollowers = async () => {
      try {
        const { data } = await axiosClient.get(`/api/users/${userId}/followers`, { params: { limit } })
        setFollowers(data.followers || [])
        setFollowersCursor((data.followers || []).slice(-1)[0]?._id || null)
        setFollowersHasMore(Boolean(data.followers && data.followers.length === limit))
      } catch (e) { }
    }
    const loadFollowing = async () => {
      try {
        const { data } = await axiosClient.get(`/api/users/${userId}/following`, { params: { limit } })
        setFollowing(data.following || [])
        setFollowingCursor((data.following || []).slice(-1)[0]?._id || null)
        setFollowingHasMore(Boolean(data.following && data.following.length === limit))
      } catch (e) { }
    }
    loadFollowers()
    loadFollowing()
    const loadUser = async () => {
      try {
        const { data } = await axiosClient.get(`/api/users/${userId}`)
        setProfileUser(data.user || null)
      } catch (e) { }
    }
    loadUser()
    const loadMyFollowing = async () => {
      try {
        if (!user?.id) return
        const { data } = await axiosClient.get(`/api/users/${user.id}/following`, { params: { limit: 1000 } })
        const ids = (data.following || []).map(it => String(it.followeeId?._id || it.followeeId))
        setMyFollowingSet(new Set(ids))
      } catch (e) { }
    }
    loadMyFollowing()
  }, [userId])

  const loadMoreFollowers = async () => {
    if (!followersHasMore) return
    try {
      const { data } = await axiosClient.get(`/api/users/${userId}/followers`, { params: { limit, cursor: followersCursor } })
      const next = data.followers || []
      setFollowers(prev => [...prev, ...next])
      setFollowersCursor(next.slice(-1)[0]?._id || followersCursor)
      setFollowersHasMore(Boolean(next.length === limit))
    } catch (e) { }
  }

  const loadMoreFollowing = async () => {
    if (!followingHasMore) return
    try {
      const { data } = await axiosClient.get(`/api/users/${userId}/following`, { params: { limit, cursor: followingCursor } })
      const next = data.following || []
      setFollowing(prev => [...prev, ...next])
      setFollowingCursor(next.slice(-1)[0]?._id || followingCursor)
      setFollowingHasMore(Boolean(next.length === limit))
    } catch (e) { }
  }

  const followersCount = followers.length
  const followingCount = following.length
  const mutualFollowing = useMemo(() => {
    if (isOwn) return []
    const mySet = myFollowingSet || new Set()
    return (following || []).filter(it => {
      const tid = String(it.followeeId?._id || it.followeeId)
      return mySet.has(tid)
    })
  }, [following, myFollowingSet, isOwn])
  const mutualFollowersCount = mutualFollowing.length

  const renderConnectionAction = (targetId, opts = {}) => {
    const { theyFollowMe = false, forceFollowing = false } = opts
    if (user?.id && String(user.id) === String(targetId)) {
      return null
    }
    const isFollowing = Boolean(forceFollowing || (followingSet && targetId && followingSet.has(String(targetId))))
    if (isFollowing) {
      return (
        <Button $variant="outline" style={{ width: '120px' }} onClick={async () => { try { await unfollow(targetId) } catch (e) { toast.error(e?.response?.data?.message || 'Error') } }}>
          {t('actions.following')}
        </Button>
      )
    }
    return (
      <Button $variant="default" style={{ width: '120px' }} onClick={async () => { try { await follow(targetId) } catch (e) { const code = e?.response?.data?.code; if (code === 'CANNOT_FOLLOW_SELF') { toast.warn(t('messages.selfFollowNotAllowed')) } else { toast.error(e?.response?.data?.message || 'Error') } } }}>
        {theyFollowMe ? t('actions.followAlso') : t('actions.follow')}
      </Button>
    )
  }

  const handleBack = () => {
    const from = location.state?.from || searchParams.get('from')
    if (from === 'profile') {
      const ret = location.state?.returnTo || `/user/${userId}`
      navigate(ret)
      return
    }
    if (from === 'event') {
      const eventId = location.state?.eventId || searchParams.get('eventId')
      const slug = location.state?.slug || searchParams.get('slug')
      if (eventId && slug) {
        navigate(`/events/${eventId}/${slug}`)
        return
      }
    }
    navigate(`/user/${userId}`)
  }

  return (
    <>
      <MainNavbar />
      <HeaderWrapper>
        <HeaderContainer>
          <PageHeader>
            <BackLink to={`/user/${userId}`} onClick={(e) => { e.preventDefault(); handleBack(); }}>
              <BackIcon height={10} width={10} fill={theme.colors.defaultStrong} />
              <Typography $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                {t('header.back')}
              </Typography>
            </BackLink>

            <Typography as="h1" $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold">
              {isOwn ? (
                t('header.titleOwn')
              ) : (
                <TitleLink to={`/user/${userId}`}>
                  {profileUser?.userAvatar ? (
                    <AvatarImage src={profileUser.userAvatar} alt="user avatar" />
                  ) : (
                    <AvatarPlaceholder>
                      <HelmetIcon width={28} height={28} fill={theme.colors.defaultStrong} />
                    </AvatarPlaceholder>
                  )}
                  <span>{`${profileUser?.name || ''} ${profileUser?.lastName || ''}`.trim()}</span>
                </TitleLink>
              )}
            </Typography>
            <SectionTabs>
              {!isOwn && mutualFollowersCount > 0 && (
                <SectionTab
                  to={`/user/${userId}/connections?tab=mutual${(location.state?.from || searchParams.get('from')) ? `&from=${location.state?.from || searchParams.get('from')}` : ''}${(location.state?.eventId || searchParams.get('eventId')) ? `&eventId=${location.state?.eventId || searchParams.get('eventId')}` : ''}${(location.state?.slug || searchParams.get('slug')) ? `&slug=${location.state?.slug || searchParams.get('slug')}` : ''}`}
                  className={`SectionTab ${activeTab === 'mutual' ? 'Active' : ''}`}
                >
                  <Typography
                    $variant="body-1-semibold" $variantMobile="body-2-semibold"
                    color={activeTab === 'mutual' ? theme.colors.brandMain : theme.colors.defaultWeak}
                  >
                    {t('tabs.mutual')} ({mutualFollowersCount})
                  </Typography>
                </SectionTab>
              )}
              <SectionTab
                to={`/user/${userId}/connections?tab=followers${(location.state?.from || searchParams.get('from')) ? `&from=${location.state?.from || searchParams.get('from')}` : ''}${(location.state?.eventId || searchParams.get('eventId')) ? `&eventId=${location.state?.eventId || searchParams.get('eventId')}` : ''}${(location.state?.slug || searchParams.get('slug')) ? `&slug=${location.state?.slug || searchParams.get('slug')}` : ''}`}
                className={`SectionTab ${activeTab === 'followers' ? 'Active' : ''}`}
              >
                <Typography
                  $variant="body-1-semibold" $variantMobile="body-2-semibold"
                  color={activeTab === 'followers' ? theme.colors.brandMain : theme.colors.defaultWeak}
                >
                  {t('tabs.followers')} ({followersCount})
                </Typography>
              </SectionTab>
              <SectionTab
                to={`/user/${userId}/connections?tab=following${(location.state?.from || searchParams.get('from')) ? `&from=${location.state?.from || searchParams.get('from')}` : ''}${(location.state?.eventId || searchParams.get('eventId')) ? `&eventId=${location.state?.eventId || searchParams.get('eventId')}` : ''}${(location.state?.slug || searchParams.get('slug')) ? `&slug=${location.state?.slug || searchParams.get('slug')}` : ''}`}
                className={`SectionTab ${activeTab === 'following' ? 'Active' : ''}`}
              >
                <Typography
                  $variant="body-1-semibold" $variantMobile="body-2-semibold"
                  color={activeTab === 'following' ? theme.colors.brandMain : theme.colors.defaultWeak}
                >
                  {t('tabs.following')} ({followingCount})
                </Typography>
              </SectionTab>
            </SectionTabs>
          </PageHeader>
        </HeaderContainer>
      </HeaderWrapper>
      <ConnectionsWrapper>
        <Container>
          {activeTab === 'followers' && (
            followers.length === 0 ? (
              <EmptyState>
                <Typography $variant="body-1-medium" color={theme.colors.defaultMain}>
                  {isOwn ? t('empty.followersOwn') : t('empty.followersOther')}
                </Typography>
              </EmptyState>
            ) : (
              <List>
                {followers.map(item => (
                  <Row key={item._id}>
                    <UserLink to={`/user/${item.followerId?._id || item.followerId}`}>
                      {item.followerId?.userAvatar ? (
                        <AvatarImage src={item.followerId.userAvatar} alt="user avatar" />
                      ) : (
                        <AvatarPlaceholder>
                          <HelmetIcon width={20} height={20} fill={theme.colors.defaultStrong} />
                        </AvatarPlaceholder>
                      )}
                      <Typography $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultStrong}>
                        {item.followerId?.name} {item.followerId?.lastName}
                      </Typography>
                    </UserLink>
                    <ActionsCell>
                      {renderConnectionAction(String(item.followerId?._id || item.followerId), { theyFollowMe: isOwn, forceFollowing: isOwn ? Boolean(myFollowingSet && myFollowingSet.has(String(item.followerId?._id || item.followerId))) : false })}
                    </ActionsCell>
                  </Row>
                ))}
                {followersHasMore && (
                  <Actions>
                    <LoadMoreButton onClick={loadMoreFollowers}>{t('actions.loadMore')}</LoadMoreButton>
                  </Actions>
                )}
              </List>
            )
          )}

          {activeTab === 'following' && (
            following.length === 0 ? (
              <EmptyState>
                <Typography $variant="body-1-medium" color={theme.colors.defaultMain}>
                  {isOwn ? t('empty.followingOwn') : t('empty.followingOther')}
                </Typography>
              </EmptyState>
            ) : (
              <List>
                {following.map(item => (
                  <Row key={item._id}>
                    <UserLink to={`/user/${item.followeeId?._id || item.followeeId}`}>
                      {item.followeeId?.userAvatar ? (
                        <AvatarImage src={item.followeeId.userAvatar} alt="user avatar" />
                      ) : (
                        <AvatarPlaceholder>
                          <HelmetIcon width={20} height={20} fill={theme.colors.defaultStrong} />
                        </AvatarPlaceholder>
                      )}
                      <Typography $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-1-medium" color={theme.colors.defaultStrong}>
                        {item.followeeId?.name} {item.followeeId?.lastName}
                      </Typography>
                    </UserLink>
                    <ActionsCell>
                      {renderConnectionAction(String(item.followeeId?._id || item.followeeId), { theyFollowMe: Boolean(myFollowingSet && myFollowingSet.has(String(item.followeeId?._id || item.followeeId))), forceFollowing: isOwn ? true : false })}
                    </ActionsCell>
                  </Row>
                ))}
                {followingHasMore && (
                  <Actions>
                    <LoadMoreButton onClick={loadMoreFollowing}>{t('actions.loadMore')}</LoadMoreButton>
                  </Actions>
                )}
              </List>
            )
          )}

          {activeTab === 'mutual' && !isOwn && mutualFollowersCount > 0 && (
            <List>
              {mutualFollowing.map(item => (
                <Row key={item._id}>
                  <UserLink to={`/user/${item.followeeId?._id || item.followeeId}`}>
                    {item.followeeId?.userAvatar ? (
                      <AvatarImage src={item.followeeId.userAvatar} alt="user avatar" />
                    ) : (
                      <AvatarPlaceholder>
                        <HelmetIcon width={20} height={20} fill={theme.colors.defaultStrong} />
                      </AvatarPlaceholder>
                    )}
                    <Typography $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-1-medium" color={theme.colors.defaultStrong}>
                      {item.followeeId?.name} {item.followeeId?.lastName}
                    </Typography>
                  </UserLink>
                  <ActionsCell>
                    {renderConnectionAction(String(item.followeeId?._id || item.followeeId), { theyFollowMe: true, forceFollowing: true })}
                  </ActionsCell>
                </Row>
              ))}
            </List>
          )}
        </Container>
      </ConnectionsWrapper>
    </>
  )
}

export default UserConnections

const SectionWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const HeaderWrapper = styled(SectionWrapper)`
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const ConnectionsWrapper = styled(SectionWrapper)``;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;
  max-width: 1400px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`

const HeaderContainer = styled(Container)`
  padding-bottom: 0px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.sizing.sm};
`

const SectionTabs = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
`

const SectionTab = styled(Link)`
  align-items: flex-end;
  background-color: transparent;
  border-bottom: 4px solid transparent;
  display: flex;
  justify-content: center;
  padding: 0 0 16px 0;

  &.Active {
    border-bottom: 4px solid ${({ theme }) => theme.colors.brandMain};
    color: ${({ theme }) => theme.colors.brandMain};
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  overflow: hidden;
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  width: 100%;
  border: 1px dashed ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  // padding: ${({ theme }) => theme.sizing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  &:last-child {
    border-bottom: none;
  }
`

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.sm};
`

const LoadMoreButton = styled.button`
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: white;
  color: ${({ theme }) => theme.colors.defaultMain};
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: 8px 12px;
`

const UserLink = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 16px;
  width: 100%;
  background-color: white;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 12px;
  }
`

const BackLink = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: fit-content;
  transition: opacity 300ms ease-in;

  &:hover {
    opacity: 0.6;
  }
`

const TitleLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  transition: opacity 300ms ease-in;

  &:hover {
    opacity: 0.6;
  }
`

const ActionsCell = styled.div`
  padding-right: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-right: 12px;
  }
`

const AvatarImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.xs};
  object-fit: cover;
`

const AvatarPlaceholder = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`
